import { Box, Paper } from "@mui/material";
import { DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useLayoutEffect, useRef } from "react";
import { previewCustomTextAttribute, useCustomizeEmailTextDialog } from "./CustomizeEmailTextDialog.hooks";
import MentionInput from "../../common/components/mentionInput/MentionInput.tsx";
import { setFirstLetterToLowerCase } from "../../common/stringOperations.ts";
import { EmailCustomizationTypes } from "../EmailCustomization.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";

export interface CustomizeEmailTextDialogProps {
    emailType: EmailCustomizationTypes;
    onDialogClose: () => void;
}

function CustomizeEmailTextDialog(props: CustomizeEmailTextDialogProps) {
    const { emailAddress } = useUserContext();
    const {
        editorText,
        taskNotificationIntervalInWeeks,
        previewShellHtml,
        previewCustomText,
        isLoading,
        handleSave,
        isSaving,
        translateString,
        sendingEmail,
        onSendEmailClick,
        onEmailNotificationIntervalChange,
        onEmailTextChange,
    } = useCustomizeEmailTextDialog(props.emailType, props.onDialogClose);

    // The shell HTML is rendered once; only the custom-text region is updated here so the email's
    // images (and thus the dialog height) stay put as the user types.
    const previewRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        const anchor = previewRef.current?.querySelector(`[${previewCustomTextAttribute}]`);
        if (anchor) {
            anchor.innerHTML = previewCustomText;
        }
    }, [previewCustomText, previewShellHtml]);

    return (
        <DotLegalDialog
            size="md"
            open
            onDialogClose={props.onDialogClose}
            onOkClick={handleSave}
            buttonOkText={translateString("save")}
            header={translateString("customizeEmailTextHeader", {
                name: translateString(setFirstLetterToLowerCase(EmailCustomizationTypes[props.emailType].toString()) + "MailTemplate"),
            })}
            inProgress={isSaving}
            isLoading={isLoading}
            secondaryButtonText={translateString("sendTestEmail")}
            onSecondaryButtonClick={() => onSendEmailClick()}
            secondaryButtonLoading={sendingEmail}
            secondaryButtonHoverText={emailAddress}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ alignSelf: "center" }}>
                    <DotLegalHeader fontWeight={400} headerStyle="small" marginBottom={0}>
                        {translateString("customiseEmailTextDescription")}
                    </DotLegalHeader>
                </Box>

                <MentionInput
                    label={translateString("customizeEmailText")}
                    value={editorText}
                    onChange={onEmailTextChange}
                    multiline
                    rows={4}
                    disableMentions
                    debounce
                    reportEveryChange
                />

                {props.emailType === EmailCustomizationTypes.WeeklyTaskNotifications && (
                    <>
                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                verticalAlign: "center",
                                gap: 1,
                                color: theme.palette.primary.dark,
                                fontSize: theme.typography.pxToRem(14),
                            })}
                        >
                            <DotLegalHeader fontWeight={400} marginBottom={0} marginTop={0} headerStyle={"small"}>
                                {translateString("emailIsSentOut")}
                            </DotLegalHeader>

                            <Box sx={{ width: 150 }}>
                                <DotLegalSelect
                                    options={[
                                        { name: translateString("everyWeek"), id: "1" },
                                        { name: translateString("everySecondWeek"), id: "2" },
                                        { name: translateString("everyThirdWeek"), id: "3" },
                                        { name: translateString("everyFourthWeek"), id: "4" },
                                        { name: translateString("never"), id: "never" },
                                    ]}
                                    onChange={(value) => onEmailNotificationIntervalChange(value!)}
                                    label={""}
                                    noOptionsLabel={""}
                                    noMargin
                                    disableClearable
                                    selectedItem={taskNotificationIntervalInWeeks ? taskNotificationIntervalInWeeks.toString() : "never"}
                                ></DotLegalSelect>
                            </Box>
                        </Box>
                    </>
                )}

                <Box>
                    <DotLegalHeader fontWeight={400} headerStyle="small" marginBottom={1}>
                        {translateString("mailPreview")}
                    </DotLegalHeader>
                    <Paper
                        elevation={0}
                        sx={{
                            borderColor: "divider",
                            padding: 2,
                            overflow: "auto",
                            backgroundColor: "#f2f2f2",
                        }}
                    >
                        <div ref={previewRef} dangerouslySetInnerHTML={{ __html: previewShellHtml }} />
                    </Paper>
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default CustomizeEmailTextDialog;
